import {type RefCallback, useCallback, useInsertionEffect, useRef} from 'react'

/**
 * Observe a single element with an IntersectionObserver.
 *
 * Returns a callback ref that should be attached to the target element.
 * When the element mounts, unmounts, or is swapped, the observer is
 * automatically re-created / cleaned up.
 *
 * @param callback      Called when the intersection observer fires
 * @param options       IntersectionObserver options (root, rootMargin, threshold)
 */
export function useIntersectionObserver(
  callback: (entries: IntersectionObserverEntry[]) => void,
  options?: IntersectionObserverInit,
): RefCallback<Element> {
  const callbackRef = useRef(callback)
  useInsertionEffect(() => {
    callbackRef.current = callback
  }, [callback])

  return useCallback<RefCallback<Element>>(
    node => {
      if (!node) return

      const observer = new IntersectionObserver(entries => callbackRef.current(entries), options)
      observer.observe(node)
      return () => observer.disconnect()
    },
    [options],
  )
}
